import React, { useState, useEffect } from 'react';
import { systemAdminAPI } from '../../../services/api/systemAdminAPI';
import useEscKeyHandler from '../../../hooks/useEscKeyHandler';
import toast from 'react-hot-toast';
import { 
  Building2, 
  Plus, 
  Search, 
  Edit3, 
  Trash2, 
  Eye,
  CheckCircle,
  XCircle,
  MapPin,
  Phone,
  Mail,
  Globe,
  Calendar,
  Users
} from 'lucide-react';

const TenantManagement = () => {
  const [tenants, setTenants] = useState([]);
  const [filteredTenants, setFilteredTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('view'); // 'view', 'create', 'edit'

  useEffect(() => {
    loadTenants();
  }, []);

  useEffect(() => {
    filterTenants();
  }, [searchTerm, tenants]);

  // Add Esc key handler for modal
  useEscKeyHandler(showModal, () => setShowModal(false));

  const loadTenants = async () => {
    try {
      setLoading(true);
      const response = await systemAdminAPI.getAllTenants();
      // Ensure tenants is always an array
      if (Array.isArray(response.data)) {
        setTenants(response.data);
      } else if (response.data && Array.isArray(response.data.tenants)) {
        setTenants(response.data.tenants);
      } else if (response.data && response.data.success && Array.isArray(response.data.tenants)) {
        setTenants(response.data.tenants);
      } else {
        console.warn('Unexpected tenants data format:', response.data);
        setTenants([]);
      }
    } catch (error) {
      console.error('Failed to load tenants:', error);
      toast.error('Failed to load tenants');
      setTenants([]); // Ensure tenants is always an array even on error
    } finally {
      setLoading(false);
    }
  };

  const filterTenants = () => {
    if (!searchTerm) {
      setFilteredTenants(tenants);
    } else {
      const filtered = tenants.filter(tenant =>
        tenant.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        tenant.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        tenant.organization_code?.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredTenants(filtered);
    }
  };

  const handleCreateTenant = () => {
    setSelectedTenant(null);
    setModalMode('create');
    setShowModal(true);
  };

  const handleViewTenant = (tenant) => {
    setSelectedTenant(tenant);
    setModalMode('view');
    setShowModal(true);
  };

  const handleEditTenant = (tenant) => {
    setSelectedTenant(tenant);
    setModalMode('edit');
    setShowModal(true);
  };

  const handleDeleteTenant = async (tenantId) => {
    // Delete functionality not yet implemented
    toast.error('Delete functionality is not yet implemented. Please contact system administrator.');
  };

  const TenantModal = () => {
      const [formData, setFormData] = useState({
    name: '',
    tenant_type: 'Hospital',
    organization_code: '',
    description: '',
    email: '',
    phone: '',
    website: '',
    address_line1: '',
    city: '',
    state: '',
    postal_code: '',
    country: 'USA',
    brand_color: '#207CE5',
    secondary_color: '#00BCD4'
  });

    useEffect(() => {
      if (selectedTenant && (modalMode === 'edit' || modalMode === 'view')) {
        setFormData({
          name: selectedTenant.name || '',
          tenant_type: selectedTenant.tenant_type || 'Hospital',
          organization_code: selectedTenant.organization_code || '',
          description: selectedTenant.description || '',
          email: selectedTenant.email || '',
          phone: selectedTenant.phone || '',
          website: selectedTenant.website || '',
          address_line1: selectedTenant.address_line1 || '',
          city: selectedTenant.city || '',
          state: selectedTenant.state || '',
          postal_code: selectedTenant.postal_code || '',
          country: selectedTenant.country || 'USA',
          brand_color: selectedTenant.brand_color || '#207CE5',
          secondary_color: selectedTenant.secondary_color || '#00BCD4'
        });
      } else if (modalMode === 'create') {
        // Reset to defaults for create mode
        setFormData({
          name: '',
          tenant_type: 'Hospital',
          organization_code: '',
          description: '',
          email: '',
          phone: '',
          website: '',
          address_line1: '',
          city: '',
          state: '',
          postal_code: '',
          country: 'USA',
          brand_color: '#207CE5',
          secondary_color: '#00BCD4'
        });
      }
    }, [selectedTenant, modalMode]);

    const handleSubmit = async (e) => {
      e.preventDefault();
      try {
        if (modalMode === 'create') {
          await systemAdminAPI.createTenant(formData);
          toast.success('Tenant created successfully');
        } else if (modalMode === 'edit') {
          await systemAdminAPI.updateTenant(selectedTenant.id, formData);
          toast.success('Tenant updated successfully');
        }
        setShowModal(false);
        loadTenants();
      } catch (error) {
        toast.error(`Failed to ${modalMode} tenant`);
      }
    };

    const isReadOnly = modalMode === 'view';

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
        <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-semibold text-gray-900">
                {modalMode === 'create' ? 'Create New Tenant' : 
                 modalMode === 'edit' ? 'Edit Tenant' : 'Tenant Details'}
              </h2>
              <button
                onClick={() => setShowModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <XCircle className="h-6 w-6" />
              </button>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="p-6 space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Basic Information */}
              <div className="space-y-4">
                <h3 className="text-lg font-medium text-gray-900">Basic Information</h3>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Organization Name *
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#207CE5] focus:border-[#207CE5]"
                    required
                    readOnly={isReadOnly}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Organization Type
                  </label>
                  <select
                    value={formData.tenant_type}
                    onChange={(e) => setFormData({ ...formData, tenant_type: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#207CE5] focus:border-[#207CE5]"
                    disabled={isReadOnly}
                  >
                    <option value="Hospital">Hospital</option>
                    <option value="Clinic">Clinic</option>
                    <option value="Research">Research Center</option>
                    <option value="Telehealth">Telehealth</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Organization Code *
                  </label>
                  <input
                    type="text"
                    value={formData.organization_code}
                    onChange={(e) => setFormData({ ...formData, organization_code: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#207CE5] focus:border-[#207CE5]"
                    required
                    readOnly={isReadOnly}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#207CE5] focus:border-[#207CE5]"
                    readOnly={isReadOnly}
                  />
                </div>
              </div>

              {/* Contact Information */}
              <div className="space-y-4">
                <h3 className="text-lg font-medium text-gray-900">Contact Information</h3>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#207CE5] focus:border-[#207CE5]"
                    required
                    readOnly={isReadOnly}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#207CE5] focus:border-[#207CE5]"
                    readOnly={isReadOnly}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Website
                  </label>
                  <input
                    type="url"
                    value={formData.website}
                    onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#207CE5] focus:border-[#207CE5]"
                    readOnly={isReadOnly}
                  />
                </div>


              </div>
            </div>

            {/* Address Information */}
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-gray-900">Address Information</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Address Line 1
                  </label>
                  <input
                    type="text"
                    value={formData.address_line1}
                    onChange={(e) => setFormData({ ...formData, address_line1: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#207CE5] focus:border-[#207CE5]"
                    readOnly={isReadOnly}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    City
                  </label>
                  <input
                    type="text"
                    value={formData.city}
                    onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#207CE5] focus:border-[#207CE5]"
                    readOnly={isReadOnly}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    State
                  </label>
                  <input
                    type="text"
                    value={formData.state}
                    onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#207CE5] focus:border-[#207CE5]"
                    readOnly={isReadOnly}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Postal Code
                  </label>
                  <input
                    type="text"
                    value={formData.postal_code}
                    onChange={(e) => setFormData({ ...formData, postal_code: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#207CE5] focus:border-[#207CE5]"
                    readOnly={isReadOnly}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Country
                  </label>
                  <input
                    type="text"
                    value={formData.country}
                    onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#207CE5] focus:border-[#207CE5]"
                    readOnly={isReadOnly}
                  />
                </div>
              </div>
            </div>

            {/* Branding */}
            {!isReadOnly && (
              <div className="space-y-4">
                <h3 className="text-lg font-medium text-gray-900">Branding</h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Primary Brand Color
                    </label>
                    <input
                      type="color"
                      value={formData.brand_color}
                      onChange={(e) => setFormData({ ...formData, brand_color: e.target.value })}
                      className="w-full h-10 border border-gray-300 rounded-lg"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Secondary Color
                    </label>
                    <input
                      type="color"
                      value={formData.secondary_color}
                      onChange={(e) => setFormData({ ...formData, secondary_color: e.target.value })}
                      className="w-full h-10 border border-gray-300 rounded-lg"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Form Actions */}
            <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
              <button
                type="button"
                onClick={() => setShowModal(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                {isReadOnly ? 'Close' : 'Cancel'}
              </button>
              {!isReadOnly && (
                <button
                  type="submit"
                  className="px-4 py-2 text-sm font-medium text-white bg-[#207CE5] border border-transparent rounded-lg hover:bg-[#0B1C2C]"
                >
                  {modalMode === 'create' ? 'Create Tenant' : 'Update Tenant'}
                </button>
              )}
            </div>
          </form>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#207CE5]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Tenant Management</h1>
          <p className="text-gray-600">Manage all healthcare organizations in the system</p>
        </div>
        <button
          onClick={handleCreateTenant}
          className="flex items-center space-x-2 px-4 py-2 bg-[#207CE5] text-white rounded-lg hover:bg-[#0B1C2C] transition-colors"
        >
          <Plus className="h-4 w-4" />
          <span>Add New Tenant</span>
        </button>
      </div>

      {/* Search and Filters */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center space-x-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search tenants by name, email, or code..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#207CE5] focus:border-[#207CE5]"
            />
          </div>
        </div>
      </div>

      {/* Tenants Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {Array.isArray(filteredTenants) && filteredTenants.map((tenant) => (
          <div key={tenant.id} className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="p-6">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-12 bg-gradient-to-br from-[#207CE5] to-[#00BCD4] rounded-lg flex items-center justify-center">
                    <Building2 className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">{tenant.name}</h3>
                    <p className="text-sm text-gray-500">{tenant.organization_code}</p>
                  </div>
                </div>
                <div className="flex items-center">
                  {tenant.status === 'active' ? (
                    <CheckCircle className="h-5 w-5 text-green-500" />
                  ) : (
                    <XCircle className="h-5 w-5 text-red-500" />
                  )}
                </div>
              </div>

              <div className="space-y-2 mb-4">
                <div className="flex items-center text-sm text-gray-600">
                  <Mail className="h-4 w-4 mr-2" />
                  {tenant.email || 'No email'}
                </div>
                <div className="flex items-center text-sm text-gray-600">
                  <Phone className="h-4 w-4 mr-2" />
                  {tenant.phone || 'No phone'}
                </div>
                <div className="flex items-center text-sm text-gray-600">
                  <MapPin className="h-4 w-4 mr-2" />
                  {tenant.city && tenant.state ? `${tenant.city}, ${tenant.state}` : 'No address'}
                </div>
              </div>

              <div className="flex items-center justify-between">
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-[#DCEAF5] text-[#207CE5]">
                  {tenant.tenant_type || 'Hospital'}
                </span>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => handleViewTenant(tenant)}
                    className="p-1 text-gray-400 hover:text-gray-600"
                    title="View Details"
                  >
                    <Eye className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => handleEditTenant(tenant)}
                    className="p-1 text-gray-400 hover:text-[#207CE5]"
                    title="Edit"
                  >
                    <Edit3 className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => handleDeleteTenant(tenant.id)}
                    className="p-1 text-gray-400 hover:text-red-600"
                    title="Delete"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {Array.isArray(filteredTenants) && filteredTenants.length === 0 && (
        <div className="text-center py-12">
          <Building2 className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No tenants found</h3>
          <p className="text-gray-500">
            {searchTerm ? 'Try adjusting your search criteria' : 'Get started by creating your first tenant'}
          </p>
        </div>
      )}

      {/* Modal */}
      {showModal && <TenantModal />}
    </div>
  );
};

export default TenantManagement;